// src/components/about/TechnicalCapabilities.styles.ts
import styled from 'styled-components';
import { motion } from 'framer-motion';

import { minDevice } from '../../styles/breakpoints';
export { SectionHeader, Eyebrow, EyebrowMark, Heading, Lede } from './SectionHeader.styles';

const sansStack = "'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif";
const monoStack = "ui-monospace, SFMono-Regular, 'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace";

/* ---------- Section shell ---------- */

export const CapabilitiesRoot = styled.section`
  padding: ${({ theme }) => theme.spacing['3xl']} 0;
  background: ${({ theme }) => theme.background};
  color: ${({ theme }) => theme.text};
  border-top: 1px solid ${({ theme }) => theme.borderColor};
`;

export const CapabilitiesContainer = styled.div`
  width: 90%;
  max-width: 1120px;
  margin: 0 auto;
`;

/* ---------- Register ---------- */

export const Stack = styled(motion.div)`
  border-top: ${({ theme }) => theme.cardBorder};
`;

export const Layer = styled(motion.div)`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.lg} 0.5rem;
  border-bottom: ${({ theme }) => theme.cardBorder};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  transition: background-color 0.2s;

  &:hover {
    background: ${({ theme }) => theme.cardBg};
  }

  @media ${minDevice.laptop} {
    grid-template-columns: minmax(0, 21rem) minmax(0, 1fr);
    gap: 2.5rem;
    padding: ${({ theme }) => theme.spacing.lg} 0.75rem;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/* ---------- Layer identity ---------- */

export const LayerHead = styled.div`
  display: grid;
  grid-template-columns: auto auto minmax(0, 1fr);
  align-items: start;
  gap: 0.5rem 0.9rem;
  min-width: 0;
`;

export const LayerIndex = styled.span`
  font-family: ${monoStack};
  font-size: 0.75rem;
  line-height: 2.1;
  letter-spacing: 0.1em;
  color: ${({ theme }) => theme.accent};
  opacity: 0.8;
`;

export const IconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.1rem;
  height: 2.1rem;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.surface};
  border: ${({ theme }) => theme.cardBorder};
  color: ${({ theme }) => theme.accent};
  transition: border-color 0.2s;

  svg {
    font-size: 0.95rem;
  }

  ${Layer}:hover & {
    border-color: ${({ theme }) => theme.accent};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const HeadText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;
`;

export const LayerTitle = styled.h3`
  margin: 0;
  font-family: ${sansStack};
  font-size: 1.05rem;
  line-height: 1.4;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.text};
`;

export const LayerRole = styled.p`
  margin: 0;
  max-width: 34ch;
  font-family: ${sansStack};
  font-size: 0.88rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.secondary};
  opacity: 0.85;
`;

/* ---------- Technology clusters ---------- */

export const LayerBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  min-width: 0;
`;

export const ChipList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  list-style: none;
  margin: 0;
  padding: 0;
`;

export const Chip = styled.li`
  font-family: ${monoStack};
  font-size: 0.75rem;
  line-height: 1.5;
  padding: 0.32rem 0.6rem;
  color: ${({ theme }) => theme.colors.secondary};
  background: ${({ theme }) => theme.surface};
  border: ${({ theme }) => theme.cardBorder};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  transition: color 0.2s ease, border-color 0.2s ease, transform 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.text};
    border-color: ${({ theme }) => theme.accent};
    transform: translateY(-1px);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover {
      transform: none;
    }
  }
`;

export const ClusterDivider = styled.span`
  display: block;
  width: 2.5rem;
  height: 2px;
  margin: 0.25rem 0 0.1rem;
  background: ${({ theme }) => theme.accent};
`;

export const ClusterLabel = styled.span`
  font-family: ${monoStack};
  font-size: 0.68rem;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.secondary};
  opacity: 0.75;
`;

/* ---------- Scope note ---------- */

export const CapabilitiesNote = styled.p`
  margin: ${({ theme }) => theme.spacing.xl} 0 0;
  max-width: 78ch;
  font-family: ${sansStack};
  font-size: 0.88rem;
  line-height: 1.75;
  color: ${({ theme }) => theme.colors.secondary};

  strong {
    font-weight: 600;
    color: ${({ theme }) => theme.text};
  }
`;